// Cosmetic mirror of the Go formula/document lexer (internal/engine).
// This tokenizer ONLY picks syntax colors per keystroke — it is never the
// authority on validity. Errors and final say come from POST
// /api/scheme/doc/check (the Go parser). If the two ever disagree, the
// worst case is a briefly wrong color, never a wrong save.
//
// Token kinds match TokenKind in Go (= CSS class suffix), plus two local
// cosmetic refinements: an ident directly followed by "(" is "func", and
// the scheme-document reserved words are "keyword".

const KEYWORDS = new Set(['calc', 'slip', 'input', 'angka', 'teks', 'pendapatan', 'potongan', 'info', 'ket', 'skiptotal', 'showif']);

const isDigit = (c) => c >= '0' && c <= '9';
const isIdentStart = (c) => (c >= 'a' && c <= 'z') || (c >= 'A' && c <= 'Z') || c === '_';
const isIdentPart = (c) => isIdentStart(c) || isDigit(c);

/** Tokenize src; returns [{kind, start, end}] with char offsets, gaps = whitespace. */
export function lex(src) {
  const toks = [];
  let i = 0;
  const emit = (kind, start) => toks.push({ kind, start, end: i });

  while (i < src.length) {
    const c = src[i];
    const start = i;

    if (c === ' ' || c === '\t' || c === '\r' || c === '\n') { i++; continue; }

    if (c === '#') { // comment to end of line
      while (i < src.length && src[i] !== '\n') i++;
      emit('comment', start);
      continue;
    }

    if (isDigit(c)) {
      while (i < src.length && isDigit(src[i])) i++;
      if (src[i] === '.') { i++; while (i < src.length && isDigit(src[i])) i++; }
      if (src[i] === '%') { i++; emit('percent', start); } else emit('number', start);
      continue;
    }

    if (c === '"') { // no escapes; ends at " or newline (mirrors Go)
      i++;
      while (i < src.length && src[i] !== '"' && src[i] !== '\n') i++;
      if (src[i] === '"') i++;
      emit('string', start);
      continue;
    }

    if (isIdentStart(c)) {
      while (i < src.length && isIdentPart(src[i])) i++;
      const word = src.slice(start, i);
      if (word === 'and' || word === 'or') emit('op', start);
      else if (KEYWORDS.has(word)) emit('keyword', start);
      else emit(src[i] === '(' ? 'func' : 'ident', start); // cosmetic refinement
      continue;
    }

    if ('()[],'.includes(c)) { i++; emit('paren', start); continue; }
    if (c === '@') { i++; emit('at', start); continue; } // leads a @tipe scope tag
    if ('+-*/'.includes(c)) { i++; emit('op', start); continue; }

    if (c === '=' || c === '!' || c === '<' || c === '>') {
      i++;
      if (src[i] === '=') i++;
      emit('op', start); // bare = / ! squiggled by the server check
      continue;
    }

    i++;
    emit('error', start);
  }
  return toks;
}

/**
 * The Go lexer reports UTF-8 BYTE offsets; JS strings index UTF-16 code
 * units. Returns a byte→char converter for src (identity fast path when
 * src is pure ASCII, which covers almost every real formula).
 */
export function makeByteToChar(src) {
  if (/^[\x00-\x7f]*$/.test(src)) return (b) => b;
  const map = []; // map[byteOffset] = char offset
  let byte = 0;
  for (let ci = 0; ci < src.length; ci++) {
    const cp = src.codePointAt(ci);
    const n = cp < 0x80 ? 1 : cp < 0x800 ? 2 : cp < 0x10000 ? 3 : 4;
    for (let k = 0; k < n; k++) map[byte + k] = ci;
    byte += n;
    if (cp > 0xffff) ci++; // surrogate pair occupies two UTF-16 units
  }
  map[byte] = src.length;
  return (b) => map[Math.min(b, byte)] ?? src.length;
}
